import React, { Fragment, memo, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { handleFreeze } from '../../utils/common';
import { useDispatch, useSelector } from 'react-redux';
import { getPartyMasterAccountWiseCustom } from '../../graphql';
import { API, graphqlOperation } from 'aws-amplify';

import './VoucherBTNnew.css';
import AddPNFDebitNote from '../modals/AddPNFDebitNote';
import AddPNFCreditNote from '../modals/AddPNFCreditNote';
import AddPNFSales from '../modals/AddPNFSales';
import AddPNFPurchase from '../modals/AddPNFPurchase';
import AddPNFJournal from '../modals/AddPNFJournal';
import AddBalanceSheetPayment from '../modals/AddBalanceSheetPayment';
import AddBalanceSheetReceipt from '../modals/AddBalanceSheetReceipt';
import { Popover, Tooltip } from '@mui/material';
import { setNavigationBlocked } from '../../redux/reducers/controllNavigation';

const MODALS = {
  debitNote: AddPNFDebitNote,
  creditNote: AddPNFCreditNote,
  sales: AddPNFSales,
  purchase: AddPNFPurchase,
  journal: AddPNFJournal,
  payment: AddBalanceSheetPayment,
  receipt: AddBalanceSheetReceipt,
};

const BUTTONS = {
  debitNote: 'Debit Note',
  creditNote: 'Credit Note',
  sales: 'Sale/Income',
  purchase: 'Purchase/Expense',
  receipt: 'Receipt',
  journal: 'Journal',
  payment: 'Payment',
};

function VoucherBTNnew({ item, theme, setVisi }) {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const [accountDetails, setAccountDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const Freeze = useSelector((state) => state.userPrefrenceandReducer?.financialYear?.freezestatus);

  const popoverRef = useRef(null);

  const open = Boolean(anchorEl);
  const id = open ? `popover-${item?.id}` : undefined;

  const pathParts = useMemo(() => (item?.category !== 'Item' ? item?.path?.split('/') || [] : []), [item]);

  const handleClick = useCallback((e) => setAnchorEl(e.currentTarget), []);
  const handlePopoverClose = useCallback(() => setAnchorEl(null), []);
  const handleCloseModal = useCallback(() => {
    setActiveModal(null);
    setOpenModal(false);
    dispatch(setNavigationBlocked(false));
  }, []);

  const handleOutsideClick = useCallback(
    (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target) && !anchorEl?.contains(e.target)) {
        handlePopoverClose();
      }
    },
    [anchorEl, handlePopoverClose]
  );

  const handleButtonClick = useCallback(
    (modalKey) => {
      setActiveModal(modalKey);
      setOpenModal(true);
      handlePopoverClose();
      if(modalKey){
        localStorage.setItem('modalOpen', true);
      }
    },
    [handlePopoverClose]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [open, handleOutsideClick]);

  useEffect(() => {
    if (!activeModal) return;

    const isBank = pathParts.includes('17') || pathParts.includes('29');
    const isCash = pathParts.includes('16');
    const type = isBank
      ? activeModal === 'receipt'
        ? 'BankReceipt'
        : 'BankPayment'
      : isCash
      ? activeModal === 'receipt'
        ? 'CashReceipt'
        : 'CashPayment'
      : undefined;

    const itemDetails = {
      accountsid: item?.id,
      label: item?.name,
      groupid: item?.parent,
      balance: item?.balance,
      accounttypeflag: item?.accounttypeflag,
      type,
    };

    const isItem = item?.category === 'Item';
    const requiresParty = ['15', '7'].some((v) => pathParts.includes(v));
    const isBasicAccount = !['receipt', 'payment', 'journal'].includes(activeModal);

    if (requiresParty && isBasicAccount) {
      getPartyDetails();
    } else {
      setAccountDetails(
        isItem
          ? {
              accountsid: item?.id,
              label: item?.name,
              groupid: item?.parent,
              itemcode: item?.itemcode,
              type: item?.type,
            }
          : itemDetails
      );
    }
  }, [activeModal, item, pathParts]);

  const getPartyDetails = async () => {
    try {
      setLoading(true);
      const res = await API.graphql(
        graphqlOperation(getPartyMasterAccountWiseCustom, {
          accountsid: item?.id,
        })
      );
      const party = res?.data?.getPartyMasterAccountWise?.[0];
      if (party) {
        setAccountDetails({
          label: party?.partydisplayname,
          value: party.partyid,
          legalName: party?.partylegalname,
          email: party?.email,
          consigneeGSTIN: party.deliveryconsigneegstin,
          consigneeName: party.deliveryconsigneename,
          accountsID: party.accountsid,
          gstin: party.relatedpartygstdetails?.[0]?.gstin,
          placeOfSupply: party?.placeofsupply,
          PartyTypeCd: party?.partytypecd,
          creditPeriod: party.relatedpartycreditdetails?.[0]?.creditperiodduration,
          gstCategory: party?.gstcategory,
          billingCurrency: party.relatedpartycreditdetails?.[0]?.billingcurrency,
          tdsSection: party?.tdssection,
          tdsPercentage: party?.tdspercentage,
          creditLimit: Number(party.relatedpartycreditdetails?.[0]?.creditlimitamount) || 0,
        });
      }
    } catch (error) {
      console.error('Party details error:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredButtons = useMemo(() => {
    const buttonMapping = {
      sales: ['8', '29', '11', '14', '19', '22', '24'],
      purchase: ['7', '29', '8', '11', '21', '23', '38', '27', '39', '40', '41'],
      receipt: ['2', '8', '29', '30', '11', '12', '13', '14', '19', '22', '24', '40', '41', '7'],
      payment: ['2', '8', '29', '7', '30', '11', '12', '13', '14', '21', '23', '38', '27', '39', '40', '41'],
      journal: [
        "1",
        '2',
        '3',
        '29',
        '7',
        '8',
        '9',
        "10",
        '30',
        '11',
        '12',
        '14',
        '19',
        '22',
        '24',
        '21',
        '23',
        '38',
        '26',
        '27',
        '28',
        '39',
        '40',
        '41',
      ],
      debitNote: ['7', '29', '8', '11', '14', '19', '22', '24', '27', '42'],
      creditNote: ['7', '29', '8', '11', '14', '21', '23', '38', '27', '39', '43'],
    };

    const exclusions = {
      sales: ['30'],
      purchase: ['30'],
      debitNote: ['16', '17', '43'],
      creditNote: ['16', '17', '42'],
      journal: ['16','17'],
    };

    if (item?.category === 'Item') {
      return ['sales', 'purchase', 'debitNote', 'creditNote'];
    }

    return Object.entries(buttonMapping)
      .filter(
        ([key, values]) => values.some((val) => pathParts.includes(val)) && (exclusions[key] || []).every((x) => !pathParts.includes(x))
      )
      .map(([key]) => key);
  }, [item?.category, pathParts]);

  const ModalComponent = activeModal ? MODALS[activeModal] : null;

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Fragment key={item?.id}>
      {loading && (
        <div className="backdrop">
          <div className="spinner" />
        </div>
      )}
      <div className="popover-wrapper">
        <button
          style={{
            border: 0,
            // backgroundColor: theme.palette.tertiary.main,
            backgroundColor: '#ee5b2b',
            // border:"1px solid gray",
            width: 25,
            height: 25,
            borderRadius: 4,
            boxShadow: '0px 6px 8px rgba(0, 0, 0, 0.5)',
          }}
          onClick={(e) => {
            if (Freeze == 1) return handleFreeze();
            handleClick(e);
            // setMenuOpen(false)
            setVisi(false);
          }}
        >
          <Tooltip title="Create Voucher" arrow>
            <div className="voucher-box V">V</div>
          </Tooltip>
        </button>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <div className="button-group-box">
            {filteredButtons.map((key) => (
              <button
                key={key}
                className="custom-button"
                style={{ '--primary-color': theme?.palette.primary.main }}
                onClick={(e) => {
                  console.log(e.target, '====>hiii');
                  e.stopPropagation();
                  handleButtonClick(key);
                }}
              >
                {BUTTONS[key]}
              </button>
            ))}
          </div>
        </Popover>

        {/* <div
          id={id}
          ref={popoverRef}
          // style={{
          //   transform: transform,
          // }}
          className={`popover-container ${open ? "visible" : ""}`}
        >
          <div className="button-group-box">
            {filteredButtons.map((key) => (
              <button
                key={key}
                className="custom-button"
                style={{ "--primary-color": theme?.palette.primary.main }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleButtonClick(key);
                }}
              >
                {BUTTONS[key]}
              </button>
            ))}
          </div>
        </div> */}
      </div>

      {ModalComponent && openModal && (accountDetails?.accountsid || accountDetails?.value) && (
        <ModalComponent open={openModal} setOpen={handleCloseModal} accountDetails={accountDetails} onSaveComplete={handleCloseModal} />
      )}
    </Fragment>
  );
}

export default memo(VoucherBTNnew);
